import type { MouseEvent } from 'react';
import type { TFunction } from 'react-i18next';
import type { AppDispatch } from '../../app/store';
import {
  bulkRemoveMatchesFromScheduleAction,
  removeMatchFromScheduleAction,
} from './scheduleActions';
import {
  setScheduleSelection,
  toggleScheduleMatchSelected,
} from './scheduleSlice';

export function preventShiftClickTextSelection(e: {
  shiftKey: boolean;
  preventDefault(): void;
}): void {
  if (e.shiftKey) e.preventDefault();
}

/** Returns true when the click was fully handled (caller should skip placement). */
export function handleScheduleBulkSelectionClick(
  e: MouseEvent,
  matchId: number,
  dispatch: AppDispatch,
  options?: { shiftStartsMarquee?: boolean; singleClickStartsPlacement?: boolean }
): boolean {
  if (e.shiftKey) {
    e.preventDefault();
    if (options?.shiftStartsMarquee) {
      return true;
    }
    dispatch(toggleScheduleMatchSelected(matchId));
    return true;
  }

  if (options?.singleClickStartsPlacement) {
    return false;
  }

  dispatch(setScheduleSelection([matchId]));
  return false;
}

/** Ctrl/Cmd+click: bulk-unschedule selection, toggle bulk select, or quick-unschedule one match. */
export function handleScheduleCtrlClick(
  e: MouseEvent,
  matchId: number,
  selectedMatchIds: number[],
  dispatch: AppDispatch,
  isScheduled: boolean,
  t: TFunction<'translation', undefined>
): boolean {
  if (!(e.ctrlKey || e.metaKey) || e.shiftKey) {
    return false;
  }

  e.preventDefault();

  if (!isScheduled) {
    dispatch(toggleScheduleMatchSelected(matchId));
    return true;
  }

  if (selectedMatchIds.length >= 2 && selectedMatchIds.includes(matchId)) {
    void dispatch(bulkRemoveMatchesFromScheduleAction(selectedMatchIds, t));
    return true;
  }

  if (selectedMatchIds.length >= 1 && !selectedMatchIds.includes(matchId)) {
    dispatch(toggleScheduleMatchSelected(matchId));
    return true;
  }

  void dispatch(removeMatchFromScheduleAction(matchId, t));
  return true;
}

export function rectsIntersect(
  a: DOMRectReadOnly,
  b: { left: number; top: number; right: number; bottom: number }
): boolean {
  return !(a.right < b.left || a.left > b.right || a.bottom < b.top || a.top > b.bottom);
}

export function collectMatchIdsInMarquee(
  container: HTMLElement,
  marquee: { left: number; top: number; right: number; bottom: number }
): number[] {
  const ids: number[] = [];
  container.querySelectorAll<HTMLElement>('[data-match-id]').forEach((el) => {
    const rect = el.getBoundingClientRect();
    if (rectsIntersect(rect, marquee)) {
      const id = Number(el.dataset.matchId);
      if (Number.isFinite(id)) ids.push(id);
    }
  });
  return ids;
}
